import { TripEventDto } from '@acorn-portal/shared/src/services/telematicsDriverTripsApi';

/** A plain latitude/longitude pair as accepted by Google Maps components. */
export type LatLng = google.maps.LatLngLiteral;

/** Shared script id so every map on the page reuses one Google Maps script tag. */
export const GOOGLE_MAPS_SCRIPT_ID = 'google-map-script';

/** Libraries every telematics map needs. Declared once so the loader sees a stable reference. */
export const GOOGLE_MAPS_LIBRARIES: 'geometry'[] = ['geometry'];

const WHITE = '#FFFFFF';

/**
 * A decoded trip event ready to draw on the map.
 */
export interface DecodedEventPolyline {
	/** Unique event identifier. */
	eventId: string;
	/** Coordinates of the path travelled during the event. */
	path: LatLng[];
	/** Severity string from the API, used for colour coding. */
	severity: string | undefined;
	/** Point on the path where the numbered marker is placed. */
	midpoint: LatLng;
	/** Zero-based position of the event in the original events array (matches list badges). */
	index: number;
}

const isGeometryAvailable = (): boolean => Boolean(globalThis.google?.maps?.geometry?.encoding);

/**
 * Decodes a Google-encoded polyline into lat/lng literals.
 *
 * Safe to call before the Maps script has loaded: returns an empty array when the
 * geometry library is unavailable, the input is empty, or decoding throws.
 */
export const decodePolyline = (polyline: string | null | undefined): LatLng[] => {
	if (!polyline || !isGeometryAvailable()) return [];

	try {
		return google.maps.geometry.encoding.decodePath(polyline).map((point) => ({
			lat: point.lat(),
			lng: point.lng(),
		}));
	} catch (error) {
		console.error('Failed to decode polyline:', error);
		return [];
	}
};

/**
 * Returns the point at the middle of a path, or 0,0 for an empty path.
 */
export const calculateMidpoint = (path: LatLng[]): LatLng => {
	if (path.length === 0) return { lat: 0, lng: 0 };
	return path[Math.floor(path.length / 2)];
};

/**
 * Extracts map coordinates from events that carry a full location.
 * Events with a missing (or zero) latitude/longitude are ignored.
 */
export const getEventCoordinates = (events: TripEventDto[] | undefined): LatLng[] =>
	(events ?? []).flatMap((event) =>
		event.location?.latitude && event.location?.longitude
			? [{ lat: event.location.latitude, lng: event.location.longitude }]
			: [],
	);

/**
 * Fits the map viewport around all given points with uniform padding.
 * No-op when there are no points.
 */
export const fitMapToPoints = (map: google.maps.Map, points: LatLng[], padding: number): void => {
	if (points.length === 0) return;

	const bounds = new google.maps.LatLngBounds();
	points.forEach((point) => bounds.extend(point));
	map.fitBounds(bounds, { top: padding, right: padding, bottom: padding, left: padding });
};

/**
 * Builds the white, bold label used on every telematics map marker.
 */
export const buildMarkerLabel = (text: string, fontSize = '11px'): google.maps.MarkerLabel => ({
	text,
	color: WHITE,
	fontWeight: 'bold',
	fontSize,
});

/**
 * Builds the filled circle with a white border used for A/B and event markers.
 * Must be called after the Maps script has loaded (reads `google.maps.SymbolPath`).
 */
export const buildCircleMarkerIcon = (fillColor: string): google.maps.Symbol => ({
	path: google.maps.SymbolPath.CIRCLE,
	fillColor,
	fillOpacity: 1,
	strokeColor: WHITE,
	strokeWeight: 2,
	scale: 12,
});

/**
 * Decodes the polyline of every event that has one, keeping the event's original
 * index so map markers and the events list show the same numbers.
 */
export const decodeEventPolylines = (events: TripEventDto[] | undefined): DecodedEventPolyline[] =>
	(events ?? []).flatMap((event, index) => {
		if (!event.eventId || !event.polyline) return [];

		const path = decodePolyline(event.polyline);
		if (path.length === 0) return [];

		return [{ eventId: event.eventId, path, severity: event.severity, midpoint: calculateMidpoint(path), index }];
	});
